import React, { useState, useEffect } from 'react';
import {
  FaChartPie, FaCashRegister, FaIceCream, FaGift, FaTruck, FaWarehouse,
  FaMoneyBillWave, FaClipboardCheck, FaLock, FaChartLine, FaLightbulb,
  FaUsers, FaCog, FaSignOutAlt, FaChevronDown, FaChevronRight, FaLayerGroup,
  FaTasks, FaCoins, FaUserShield, FaUndoAlt, FaHistory, FaBalanceScale, FaFileInvoiceDollar, FaReceipt, FaShieldAlt, FaBars, FaTimes
} from 'react-icons/fa';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import BillingPage from './pages/BillingPage';
import ShiftsPage from './pages/ShiftsPage';
import PurchasesPage from './pages/PurchasesPage';
import AvailableStockPage from './pages/AvailableStockPage';
import CountHistoryPage from './pages/CountHistoryPage';
import SuppliersPage from './pages/SuppliersPage';
import OffersPage from './pages/OffersPage';
import ProductOffersPage from './pages/ProductOffersPage';
import ExpensesPage from './pages/ExpensesPage';
import EbPage from './pages/EbPage';
import PayrollPage from './pages/PayrollPage';
import ClosingPage from './pages/ClosingPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import SecurityPage from './pages/SecurityPage';
import InsightsPage from './pages/InsightsPage';
import SettingsPage from './pages/SettingsPage';
import ReturnsPage from './pages/ReturnsPage';
import PnLPage from './pages/PnLPage';
import ProfitHub from './pages/ProfitHub';
import DailyProfitPage from './pages/DailyProfitPage';
import MarginsPage from './pages/MarginsPage';
import HistoryPage from './pages/HistoryPage';
import SupplierLedgerPage from './pages/SupplierLedgerPage';
import CategoriesPage from './pages/CategoriesPage';
import BillHistoryPage from './pages/BillHistoryPage';
import AuditLogPage from './pages/AuditLogPage';
import LoginPage from './pages/LoginPage';
import { getCurrentUser, logout, api } from './api';
import './App.css';

// Top-level items (always visible, no group) + grouped sections (collapsible)
const TOP_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: FaChartPie, perm: null },
  { key: 'billing', label: 'Billing', icon: FaCashRegister, perm: null },
  { key: 'shifts', label: 'Shift Handover', icon: FaCashRegister, perm: null },
];

const GROUPS = [
  {
    key: 'masters', label: 'Masters', icon: FaLayerGroup,
    items: [
      { key: 'products', label: 'Products & Recipes', icon: FaIceCream, perm: 'canManageInventory' },
      { key: 'productoffers', label: 'Product Offers', icon: FaGift, perm: 'canManageInventory' },
      { key: 'offers', label: 'Offers', icon: FaGift, perm: 'canManageInventory' },
      { key: 'suppliers', label: 'Suppliers', icon: FaWarehouse, perm: 'canManagePurchases' },
      { key: 'categories', label: 'Categories', icon: FaLayerGroup, perm: 'canManageInventory' },
      { key: 'ledger', label: 'Supplier Ledger', icon: FaFileInvoiceDollar, perm: 'canManagePurchases' },
      { key: 'billhistory', label: 'Bill History', icon: FaReceipt, perm: null },
      { key: 'history', label: 'Price & Recipe History', icon: FaHistory, perm: 'canManageInventory' },
    ]
  },
  {
    key: 'operations', label: 'Operations', icon: FaTasks,
    items: [
      { key: 'purchases', label: 'Purchases', icon: FaTruck, perm: 'canManagePurchases' },
      { key: 'stockcount', label: 'Available Stock', icon: FaClipboardCheck, perm: 'canManageInventory' },
      { key: 'counthistory', label: 'Count History', icon: FaHistory, perm: 'canManageInventory' },
      { key: 'returns', label: 'Returns & Refunds', icon: FaUndoAlt, perm: 'canManagePurchases' },
    ]
  },
  {
    key: 'finance', label: 'Finance', icon: FaCoins,
    items: [
      { key: 'expenses', label: 'Expenses', icon: FaMoneyBillWave, perm:'canManageExpenses' },
  { key: 'eb', label: 'Electricity (EB)', icon: FaMoneyBillWave, perm:'canManageExpenses' },
    { key: 'payroll', label: 'Attendance & Salary', icon: FaMoneyBillWave, perm:'canManageExpenses' },
      { key: 'closing', label: 'Day Closing', icon: FaLock, perm: null },
      { key: 'profit', label: 'Profit & Reports', icon: FaChartLine, perm: 'canViewReports' },
    ]
  },
  {
    key: 'admin', label: 'Admin', icon: FaUserShield,
    items: [
      { key: 'users', label: 'Staff', icon: FaUsers, perm: 'canManageUsers' },
      { key: 'security', label: 'Security', icon: FaLock, perm: 'canManageUsers' },
      { key: 'auditlog', label: 'Audit Log', icon: FaShieldAlt, perm: 'canViewReports' },
      { key: 'settings', label: 'Settings', icon: FaCog, perm: 'canManageUsers' },
    ]
  },
];

const ALL_ITEMS = [...TOP_ITEMS, ...GROUPS.flatMap(g => g.items)];

const PAGES = {
  dashboard: DashboardPage, billing: BillingPage, shifts: ShiftsPage, products: ProductsPage, offers: OffersPage, productoffers: ProductOffersPage,
  purchases: PurchasesPage, suppliers: SuppliersPage, expenses: ExpensesPage, eb: EbPage, payroll: PayrollPage,
  stockcount: AvailableStockPage, counthistory: CountHistoryPage, closing: ClosingPage, reports: ReportsPage,
  insights: InsightsPage, users: UsersPage, security: SecurityPage, settings: SettingsPage,
  returns: ReturnsPage, pnl: PnLPage, profit: ProfitHub, dailyprofit: DailyProfitPage, margins: MarginsPage, history: HistoryPage, ledger: SupplierLedgerPage, billhistory: BillHistoryPage, auditlog: AuditLogPage, categories: CategoriesPage
};

function App() {
  const [user, setUser] = useState(getCurrentUser());
  const [shopName, setShopName] = useState('Ice Cream Shop');

  useEffect(() => {
    if (!user) return;
    api.get('/settings').then(data => {
      if (data && data.shop_name) setShopName(data.shop_name);
    }).catch(() => {});
  }, [user]);
  const isOwnerRole = (u) => !!u && ['owner', 'admin'].includes(String(u.role || '').toLowerCase());
  const [tab, setTab] = useState(() => (isOwnerRole(getCurrentUser()) ? 'dashboard' : 'billing'));
  // cashier-start-billing: staff accounts open on Billing (they cannot see the Dashboard)
  useEffect(() => {
    if (user && !isOwnerRole(user) && tab === 'dashboard') setTab('billing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  useEffect(() => {
    const go = (e) => { if (e.detail) setTab(e.detail); };
    window.addEventListener('icecream-navigate', go);
    return () => window.removeEventListener('icecream-navigate', go);
  }, []);
  const [openGroups, setOpenGroups] = useState({});
  const [navOpen, setNavOpen] = useState(false);

  if (!user) {
    return <LoginPage onLoggedIn={() => setUser(getCurrentUser())} />;
  }

  const perms = user.permissions || {};
  window.__icecreamPalette = { nav: TOP_ITEMS, perms, isOwner: isOwnerRole(user), go: setTab };
  const isCashierOnly = user.role === 'cashier';

  const itemVisible = (item) => {
    if (isCashierOnly && (item.key === 'dashboard' || item.key === 'closing')) return false;
    if (!item.perm) return true;
    return perms[item.perm];
  };

  const goTo = (key) => { setTab(key); setNavOpen(false); };

  const toggleGroup = (key) => setOpenGroups(g => (g[key] ? {} : { [key]: true }));

  const PageComponent = PAGES[tab] || DashboardPage;
  const activeLabel = ALL_ITEMS.find(i => i.key === tab)?.label || 'Dashboard';

  return (
    <div className="app-shell">
      <aside className={`sidebar ${navOpen ? 'is-open' : ''}`}>
        <div className="sidebar-brand">
          <button type="button" className="sidebar-close" onClick={() => setNavOpen(false)} aria-label="Close menu"><FaTimes /></button>
          <img src="/shop-logo.png" alt="Ice Bay x Hendy's Kitchen" className="brand-logo" />
          <div className="brand-text">{shopName}</div>
        </div>

        <nav className="sidebar-nav">
          {TOP_ITEMS.filter(itemVisible).map(item => {
            const Icon = item.icon;
            return (
              <button key={item.key} className={`sidebar-link ${tab === item.key ? 'active' : ''}`} onClick={() => goTo(item.key)}>
                <Icon className="sidebar-icon" />
                <span>{item.label}</span>
              </button>
            );
          })}

          {GROUPS.map(group => {
            const visibleItems = group.items.filter(itemVisible);
            if (visibleItems.length === 0) return null;
            const GroupIcon = group.icon;
            const isOpen = openGroups[group.key];
            return (
              <div key={group.key} className="sidebar-group">
                <button className="sidebar-group-header" onClick={() => toggleGroup(group.key)}>
                  <GroupIcon className="sidebar-icon" />
                  <span>{group.label}</span>
                  {isOpen ? <FaChevronDown className="chevron" /> : <FaChevronRight className="chevron" />}
                </button>
                <div className={`sidebar-group-items ${isOpen ? 'open' :''}`}>
                  {visibleItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.key} className={`sidebar-link nested ${tab === item.key ? 'active' : ''}`} onClick={() => goTo(item.key)}>
                        <Icon className="sidebar-icon" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{user.username.charAt(0).toUpperCase()}</div>
            <div>
              <div className="user-name">{user.username}</div>
              <div className="user-role">{user.role}</div>
            </div>
          </div>
          <button className="sidebar-logout" onClick={logout} title="Logout">
            <FaSignOutAlt />
          </button>
        </div>
      </aside>

      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} />}

      <div className="app-content">
        <header className="topbar">
          <button type="button" className="topbar-menu" onClick={() => setNavOpen(true)} aria-label="Open menu"><FaBars /></button>
          <h2>{activeLabel}</h2>
        </header>
        <main className="app-main">
          <PageComponent />
        </main>
      </div>
    </div>
  );
}

export default App;
